import { useState, useCallback, useRef } from 'react';
import { paymentService } from '../services/payment-api';
import { ANALYTICS_EVENTS, trackEvent } from '@/features/analytics';
import { usePurchaseStore } from '@/stores/purchase-store';
import { useQuizStore } from '@/stores/quiz-store';
import type { ProductPlan } from '@/features/products/types';
import type { PaymentStatus } from '../types';

export interface UseUpsellResult {
  status: PaymentStatus;
  error: string | null;
  /** Whether an upsell can be charged (we have the original subscription id). */
  canPurchase: boolean;
  purchase: (plan: ProductPlan, extra?: Record<string, unknown>) => Promise<boolean>;
}

/**
 * Charges an additional plan to the card saved during the initial purchase,
 * using the original subscription id (no new card entry required).
 */
export function useUpsell(): UseUpsellResult {
  const subscriptionId = usePurchaseStore((s) => s.paymentIntentId);
  const email = (useQuizStore((s) => s.answers.email) as string) || '';
  const [status, setStatus] = useState<PaymentStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const purchase = useCallback(
    async (plan: ProductPlan, extra?: Record<string, unknown>): Promise<boolean> => {
      if (inFlight.current) return false;
      if (!subscriptionId) {
        setError('No active order to attach this offer to.');
        return false;
      }

      inFlight.current = true;
      setStatus('processing');
      setError(null);
      trackEvent(ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
        productId: plan.id,
        price: plan.price,
        currency: plan.currency,
        metadata: { kind: 'upsell', ...extra },
      });

      try {
        const result = await paymentService.createUpsellSubscription({
          planId: plan.priceId || plan.id,
          email,
          subscriptionId,
        });
        setStatus('success');
        trackEvent(ANALYTICS_EVENTS.PURCHASE, {
          productId: plan.id,
          transactionId: result.id,
          price: plan.price,
          currency: plan.currency,
          metadata: { kind: 'upsell', ...extra },
        });
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Could not add the offer.';
        setStatus('error');
        setError(msg);
        trackEvent(ANALYTICS_EVENTS.PAYMENT_FAILED, {
          productId: plan.id,
          metadata: { kind: 'upsell', error: msg, ...extra },
        });
        return false;
      } finally {
        inFlight.current = false;
      }
    },
    [subscriptionId, email],
  );

  return { status, error, canPurchase: Boolean(subscriptionId), purchase };
}
